import { Ruler } from "@procore/core-icons";
import { useCallback, useMemo } from "react";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppSelector } from "app/redux-store-interactions";
import type { ToolbarButtonEntry } from "contexts/toolbars";
import { useEnterToolMode } from "features/toolMode/useEnterToolMode";
import SectionIcon from "media/icons/section.svg?react";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";
import { DEFAULT_MEASURE_PICKER, DEFAULT_SECTION_PICKER } from "slices/procoreUx/utils";

import { TOOLS_BUTTON_IDS } from "./utils";

/**
 * Encapsulates the shared measure/section button definitions used by both
 * ToolsMenuToolbar and the overflow flyout.
 *
 * @param onSelect Optional callback invoked after a picker is set (e.g. to close a flyout).
 */
export function useToolsButtons(onSelect?: () => void) {
    const activeToolMode = useAppSelector(selectActiveToolMode);
    const isMeasureActive = activeToolMode === ProcoreToolMode.Measure;
    const isSectionActive = activeToolMode === ProcoreToolMode.Section;
    const enterToolMode = useEnterToolMode();

    const handleMeasureClick = useCallback(() => {
        enterToolMode({ picker: DEFAULT_MEASURE_PICKER });
        void track(Events.toolbar.measureTapped, { origin: EventOrigin.Toolbar });
        onSelect?.();
    }, [enterToolMode, onSelect]);

    const handleSectionClick = useCallback(() => {
        enterToolMode({ picker: DEFAULT_SECTION_PICKER });
        void track(Events.toolbar.sectionTapped, { origin: EventOrigin.Toolbar });
        onSelect?.();
    }, [enterToolMode, onSelect]);

    const buttons = useMemo(
        (): ToolbarButtonEntry[] => [
            {
                config: {
                    id: TOOLS_BUTTON_IDS.MEASURE,
                    icon: <Ruler />,
                    onClick: handleMeasureClick,
                    tooltip: {
                        toolName: "measureTools",
                        shortcutKey: "M",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: isMeasureActive,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: TOOLS_BUTTON_IDS.SECTION,
                    icon: <SectionIcon />,
                    onClick: handleSectionClick,
                    tooltip: {
                        toolName: "sectionTools",
                        shortcutKey: "X",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: isSectionActive,
                    disabled: false,
                    visible: true,
                },
            },
        ],
        [handleMeasureClick, handleSectionClick, isMeasureActive, isSectionActive],
    );

    return { buttons, isMeasureActive, isSectionActive };
}
